import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FilePresentOutlinedIcon from "@mui/icons-material/FilePresentOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { Button, Grid, IconButton, Typography, styled } from "@mui/material";
import { filesize } from "filesize";
import { t } from "i18next";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { useImportFileEditMutation } from "../../services/productsService";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

export function FileComponent(props) {
    return (
        <Grid container justifyItems={"center"} justifyContent={"center"} sx={props.sx}>
            <Grid container direction={"row"} borderRadius={2} bgcolor={"white"} xs={10}>
                <Grid
                    container
                    xs={1.1}
                    justifyContent={"center"}
                    alignItems={"center"}
                    sx={{
                        borderRadius: 100,
                        bgcolor: "whitesmoke",
                        aspectRatio: 1,
                        m: 1,
                    }}
                >
                    <FilePresentOutlinedIcon />
                </Grid>
                <Grid
                    container
                    xs={8.5}
                    direction={"column"}
                    alignItems={"start"}
                    justifyContent={"space-between"}
                    sx={{ m: 1 }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            textWrap: "nowrap",
                            maxWidth: "100%",
                        }}
                    >
                        {props.fileName}
                    </Typography>
                    <Typography variant="caption" sx={{}}>
                        {props.fileSize}
                    </Typography>
                </Grid>
                <Grid
                    container
                    xs={1}
                    justifyContent={"center"}
                    alignItems={"center"}
                    sx={{ m: 1 }}
                >
                    <IconButton onClick={props.onDelete}>
                        <DeleteOutlineOutlinedIcon color="error" />
                    </IconButton>
                </Grid>
            </Grid>
        </Grid>
    );
}

export function ProductEditImportListDialog(props) {
    const [uploadedFile, setUploadedFile] = useState(null);

    const btnEnabled = Boolean(uploadedFile);

    const { mutate: importFile, isPending } = useImportFileEditMutation({
        onSuccess: () => {
            toast.custom(
                () => (
                    <SnackbarSuccess
                        message={t("products.ProductEditImportListDialog.alerts.importSuccess")}
                    />
                ),
                {
                    position: "bottom-right",
                },
            );
            props.onClose();
        },
        onError: (error) => {
            console.error("Failed to update products", error);
            // @ts-ignore
            const responseObj = error.response?.data;
            const message =
                responseObj.translation_key && responseObj.value
                    ? `${t(
                          `products.ProductEditImportListDialog.alerts.${responseObj.translation_key}`,
                      )}: ${responseObj.value}`
                    : responseObj.error;
            toast.custom(() => <SnackbarError message={message} />, {
                position: "bottom-right",
            });
        },
    });

    const onSubmit = useCallback(() => {
        if (!uploadedFile) {
            return;
        }
        importFile({ file: uploadedFile });
    }, [uploadedFile, importFile]);

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={props.title}
            maxWidth={"sm"}
            showDialogActions
        >
            <Grid container direction={"column"}>
                <Grid
                    container
                    minHeight={200}
                    sx={{
                        justifyContent: "center",
                        border: 1,
                        borderRadius: 3,
                        borderColor: "whitesmoke",
                        bgcolor: "whitesmoke",
                        boxShadow: "inset 0px 0px 15px rgba(0,0,0,0.05)",
                    }}
                >
                    <Grid
                        container
                        direction={"column"}
                        bgcolor={""}
                        sx={{ justifyContent: "center", alignItems: "center" }}
                    >
                        {!uploadedFile && (
                            <Button
                                component="label"
                                variant="contained"
                                tabIndex={-1}
                                startIcon={<CloudUploadIcon />}
                            >
                                {t("products.ProductEditImportListDialog.fileUpload")}
                                <VisuallyHiddenInput
                                    type="file"
                                    onChange={(event) => {
                                        setUploadedFile(event.target.files?.item(0) || null);
                                    }}
                                />
                            </Button>
                        )}
                        {!!uploadedFile && (
                            <FileComponent
                                fileName={uploadedFile.name}
                                fileSize={filesize(uploadedFile.size)}
                                onDelete={() => setUploadedFile(null)}
                            />
                        )}
                        <Typography variant="body2" sx={{ color: "gray", mt: 2 }}>
                            {t("products.ProductEditImportListDialog.maxFileSize")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "gray", mt: 1 }}>
                            {t("products.ProductEditImportListDialog.maxNameLength")}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item xs={12} sx={{ mt: 4 }}>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        loading={isPending}
                        onClick={onSubmit}
                        disabled={!btnEnabled}
                    >
                        {t("products.ProductEditImportListDialog.importButton")}
                    </LoadingButton>
                </Grid>
            </Grid>
        </BasicDialog>
    );
}
